import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Trophy, Users, Swords, Calendar, Settings, ChevronLeft, ChevronRight } from "lucide-react";
import { Loader, btn } from "@/components/Page";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";

const FIGHTERS_PER_PAGE = 20;

export default function CircuitProfile() {
  const { circuitId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [circuit, setCircuit] = useState(null);
  const [rosterFighters, setRosterFighters] = useState([]);
  const [isEditor, setIsEditor] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    loadCircuit();
  }, [circuitId]);

  const loadCircuit = async () => {
    try {
      const { data } = await api.get(`/arena/circuits/${circuitId}`);
      setCircuit(data.circuit);
      setRosterFighters(data.roster_fighters);
      setIsEditor(data.is_editor);
    } catch (e) {
      toast.error(apiErr(e));
      navigate("/arena-circuit/circuits");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loader />;
  if (!circuit) return null;

  // Pagination
  const totalPages = Math.ceil(rosterFighters.length / FIGHTERS_PER_PAGE);
  const startIndex = (currentPage - 1) * FIGHTERS_PER_PAGE;
  const endIndex = startIndex + FIGHTERS_PER_PAGE;
  const currentFighters = rosterFighters.slice(startIndex, endIndex);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/arena-circuit/circuits")}
          className={btn() + " bg-zinc-800 hover:bg-zinc-700"}
        >
          <ArrowLeft size={16} />
        </button>
        <div className="flex-1">
          <h1 className="font-cinzel text-3xl text-amber-300 tracking-wide mb-1">
            {circuit.name}
          </h1>
          <p className="text-zinc-400">
            Organized by <span className="text-amber-400">{circuit.editor_name}</span>
          </p>
        </div>
        {isEditor && (
          <button
            onClick={() => navigate("/arena-circuit/my-circuit")}
            className={btn() + " flex items-center gap-2"}
          >
            <Settings size={16} />
            Manage Circuit
          </button>
        )}
      </div>

      {/* Status Banner */}
      <div className={`border rounded p-4 ${
        circuit.status === "recruiting"
          ? "bg-emerald-950/20 border-emerald-700/40"
          : circuit.status === "active"
          ? "bg-amber-950/20 border-amber-700/40"
          : "bg-zinc-900/50 border-zinc-800"
      }`}>
        <div className="flex items-center gap-2">
          <Calendar size={16} className={
            circuit.status === "recruiting" ? "text-emerald-400" :
            circuit.status === "active" ? "text-amber-400" : "text-zinc-500"
          } />
          <span className={`font-cinzel text-sm uppercase tracking-wide ${
            circuit.status === "recruiting" ? "text-emerald-300" :
            circuit.status === "active" ? "text-amber-300" : "text-zinc-400"
          }`}>
            {circuit.status === "recruiting" ? "Recruiting Gladiators" :
             circuit.status === "active" ? "Not Recruiting" : "Closed"}
          </span>
        </div>
      </div>

      {/* Description */}
      {circuit.description && (
        <div className="border border-amber-900/30 bg-zinc-950 p-6">
          <h3 className="font-cinzel text-xl text-amber-300 mb-3 uppercase tracking-wide">
            About This Circuit
          </h3>
          <p className="text-zinc-300 leading-relaxed">{circuit.description}</p>
        </div>
      )}

      {/* Stats */}
      <div className="border border-amber-900/30 bg-zinc-950 p-6">
        <h3 className="font-cinzel text-xl text-amber-300 mb-4 uppercase tracking-wide">
          Circuit Stats
        </h3>
        <div className="grid grid-cols-3 gap-6 text-center">
          <div>
            <div className="text-3xl font-cinzel text-amber-400">
              {circuit.stats?.total_fighters || 0}
            </div>
            <div className="text-xs uppercase tracking-widest text-zinc-500 mt-1">
              Gladiators
            </div>
          </div>
          <div>
            <div className="text-3xl font-cinzel text-amber-400">
              {circuit.stats?.total_bouts || 0}
            </div>
            <div className="text-xs uppercase tracking-widest text-zinc-500 mt-1">
              Total Bouts
            </div>
          </div>
          <div>
            <div className="text-3xl font-cinzel text-amber-400">
              {circuit.stats?.fight_cards || 0}
            </div>
            <div className="text-xs uppercase tracking-widest text-zinc-500 mt-1">
              Fight Cards
            </div>
          </div>
        </div>
      </div>

      {/* Roster */}
      <div className="border border-amber-900/30 bg-zinc-950 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-cinzel text-xl text-amber-300 uppercase tracking-wide flex items-center gap-2">
            <Users size={20} />
            Circuit Roster
            {rosterFighters.length > 0 && (
              <span className="text-sm text-zinc-500">({rosterFighters.length})</span>
            )}
          </h3>
          {totalPages > 1 && (
            <div className="text-sm text-zinc-400">
              Page {currentPage} of {totalPages}
            </div>
          )}
        </div>

        {rosterFighters.length === 0 ? (
          <div className="text-center py-8 text-zinc-500 text-sm">
            No gladiators under contract yet
          </div>
        ) : (
          <>
            {/* Simple List View */}
            <div className="space-y-2">
              {currentFighters.map((fighter) => (
                <div
                  key={fighter.id}
                  onClick={() => navigate(`/arena-circuit/fighter/${fighter.id}`)}
                  className="flex items-center justify-between p-3 border border-zinc-800 bg-zinc-900/30 hover:border-amber-700/50 hover:bg-zinc-900/50 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-3 flex-1">
                    {fighter.corona_holder && (
                      <Trophy size={16} className="text-amber-400 flex-shrink-0" />
                    )}
                    <div className="flex-1">
                      <div className="font-cinzel text-amber-300">{fighter.name}</div>
                      <div className="text-xs text-zinc-500 capitalize">{fighter.cls}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-6 text-sm text-zinc-400">
                    <span className="flex items-center gap-1">
                      <Trophy size={12} />
                      {fighter.record.wins}W / {fighter.record.losses}L
                    </span>
                    <span className="text-zinc-600">|</span>
                    <span>{fighter.record.bouts} Bouts</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-zinc-800">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className={`flex items-center gap-2 px-4 py-2 text-sm ${
                    currentPage === 1
                      ? "text-zinc-600 cursor-not-allowed"
                      : "text-zinc-300 hover:text-amber-300"
                  }`}
                >
                  <ChevronLeft size={16} />
                  Previous
                </button>
                <div className="text-sm text-zinc-400">
                  Showing {startIndex + 1}-{Math.min(endIndex, rosterFighters.length)} of {rosterFighters.length}
                </div>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className={`flex items-center gap-2 px-4 py-2 text-sm ${
                    currentPage === totalPages
                      ? "text-zinc-600 cursor-not-allowed"
                      : "text-zinc-300 hover:text-amber-300"
                  }`}
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
